import React, { useState, useContext } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import SegmentIcon from '@mui/icons-material/Segment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { UserContext } from '../App';
import SmartLabLogo from '../img/SmartLabLogo.png';
import Header from './Header';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';

const DRAWER_WIDTH = 305;

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);

  const toggleDrawer = () => setOpen(!open);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAdmin } = useContext(UserContext);

  const topMenuItems = isAdmin
    ? [
        { text: 'Dashboard', icon: <SpaceDashboardIcon fontSize="small" />, path: '/dashboard' },
        { text: 'Reports', icon: <SegmentIcon fontSize="small" />, path: '/reports' },
        { text: 'User Registry', icon: <PersonAddIcon fontSize="small" />, path: '/userregistry' },
        { text: 'Semester', icon: <CalendarMonthRoundedIcon fontSize="small" />, path: '/semester' },
      ]
    : [
        { text: 'Dashboard', icon: <SpaceDashboardIcon fontSize="small" />, path: '/dashboard' },
        { text: 'Reports', icon: <SegmentIcon fontSize="small" />, path: '/reports' },
      ];

  const bottomMenuItems = isAdmin
    ? [
        {
          text: 'Preferences',
          icon: <SettingsIcon fontSize="small" />,
          path: '/preferences',
        },
        {
          text: 'Logout',
          icon: <LogoutIcon fontSize="small" />,
          action: () => {
            setOpenLogoutConfirm(true);
          },
        },
      ]
    : [
        {
          text: 'Logout',
          icon: <LogoutIcon fontSize="small" />,
          action: () => {
            setOpenLogoutConfirm(true);
          },
        },
      ];

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setOpenLogoutConfirm(false);
      if (isMobile) setOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
      setOpenLogoutConfirm(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? open : true}
        onClose={toggleDrawer}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: '#f7fafc',
            border: 'none',
            boxShadow: 'none',
            borderRadius: 0,
            '& .MuiListItemIcon-root': {
              minWidth: '40px',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 0.5,
            pt: 2.2,
            pb: 1.2,
            backgroundColor: '#f7fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
          }}
        >
          <img
            src={SmartLabLogo}
            alt="SmartLab Logo"
            style={{
              width: '100%',
              maxWidth: '290px',
              height: 'auto',
              display: 'block',
              marginTop: '-11px',
            }}
          />
        </Box>

        <Divider
          sx={{
            background:
              'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)',
            height: '1px',
            border: 'none',
            margin: '0 12px',
            marginBottom: '11px',
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <List>
            {topMenuItems.map((item) => {
              const isSelected = location.pathname === item.path;

              return (
                <ListItemButton
                  key={item.text}
                  selected={isSelected}
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setOpen(false);
                  }}
                  sx={{
                    pl: 3,
                    '&.Mui-selected': {
                      backgroundColor: '#ffffff',
                      borderRadius: '15px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      borderRadius: '15px',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      fontSize: '0.9rem !important',
                      backgroundColor: isSelected ? '#130f72' : '#ffffff',
                      color: isSelected ? '#ffc400' : '#130f72',
                      borderRadius: '15px',
                      p: 1,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '15px',
                      color: isSelected ? '#000000' : '#90a4ae',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Box sx={{ flexGrow: 1 }} />

          <List>
            {bottomMenuItems.map((item) => {
              const isSelected = item.path ? location.pathname === item.path : false;

              return (
                <ListItemButton
                  key={item.text}
                  selected={isSelected}
                  onClick={() => {
                    if (item.path) {
                      navigate(item.path);
                      if (isMobile) setOpen(false);
                    }

                    if (item.action) {
                      item.action();
                    }
                  }}
                  sx={{
                    pl: 3,
                    '&.Mui-selected': {
                      backgroundColor: '#ffffff',
                      borderRadius: '15px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      borderRadius: '15px',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      fontSize: '0.9rem !important',
                      backgroundColor: isSelected ? '#130f72' : '#ffffff',
                      color: isSelected ? '#ffc400' : '#130f72',
                      borderRadius: '15px',
                      p: 1,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '15px',
                      color: isSelected ? '#000000' : '#90a4ae',
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{ flexGrow: 1, py: 0, px: 0, backgroundColor: '#f7fafc' }}
      >
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2 }}>
            <IconButton onClick={toggleDrawer}>
              <MenuIcon />
            </IconButton>

            <Box
              component="img"
              src={SmartLabLogo}
              alt="SmartLab Logo"
              sx={{
                ml: 2,
                width: '235px',
                height: 'auto',
                display: 'block',
              }}
            />
          </Box>
        )}

        <Header />
        <Outlet />
      </Box>

      <Dialog open={openLogoutConfirm} onClose={() => setOpenLogoutConfirm(false)}>
        <DialogTitle
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontWeight: 700,
            color: '#130f72',
          }}
        >
          Confirm Logout
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: 'Poppins, sans-serif',
              marginX: 5,
            }}
          >
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenLogoutConfirm(false)}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              color: '#130f72',
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleLogoutConfirm}
            autoFocus
            color="error"
            sx={{
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;